// Who is jamming right now — the game layer's "active now", from the one
// place presence is known: each open jam room's live sockets, which the core
// API's jam list reads (GET /api/jams, participants per open room, observers
// never counted). Polled; a jam is live while anyone is in it.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { JamParticipant, JamSummary } from "@shared/api-types.js";
import { webApi } from "@web-shared/lib/web-api";

export type ListedJam = JamSummary & { creatorUsername: string; participants?: JamParticipant[] };

export interface LiveJam extends ListedJam {
  participants: JamParticipant[];
}

/** Open jams with someone in them, busiest first. */
export function liveJamsOf(jams: ListedJam[]): LiveJam[] {
  return jams
    .filter((j): j is LiveJam => j.status === "open" && (j.participants?.length ?? 0) > 0)
    .sort((a, b) => b.participants.length - a.participants.length || b.lastSeenAt.localeCompare(a.lastSeenAt));
}

export function useLiveJams(): LiveJam[] {
  const jams = useQuery({
    queryKey: ["web", "jams"],
    refetchInterval: 60_000,
    staleTime: 30_000,
    queryFn: () => webApi<ListedJam[]>("/jams"),
  });
  return useMemo(() => liveJamsOf(jams.data ?? []), [jams.data]);
}

/** userId → the jam they are in right now. */
export function useLiveProducers(): Map<string, LiveJam> {
  const live = useLiveJams();
  return useMemo(() => {
    const byUser = new Map<string, LiveJam>();
    for (const jam of live) for (const p of jam.participants) if (!byUser.has(p.userId)) byUser.set(p.userId, jam);
    return byUser;
  }, [live]);
}

export function jamPath(jamId: string): string {
  return `/jams/${encodeURIComponent(jamId)}`;
}
